import { File, FileImage, FileSpreadsheet, FileText } from 'lucide-react';
import { ElementType } from 'react';

const BYTES_IN_KILOBYTE = 1024;

const SIZE_UNITS = ['B', 'KB', 'MB', 'GB'] as const;

const EXTENSION_LABEL_BY_MEDIA_TYPE: Record<string, string> = {
  'application/pdf': 'pdf',
  'image/png': 'png',
  'image/jpeg': 'jpg',
  'image/webp': 'webp',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
};

const ICON_BY_MEDIA_TYPE: Record<string, ElementType> = {
  'application/pdf': FileText,
  'image/png': FileImage,
  'image/jpeg': FileImage,
  'image/webp': FileImage,
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': FileSpreadsheet,
};

const getExtensionFromFilename = (filename: string): string | null => {
  const parts = filename.split('.');
  return parts.length > 1 ? (parts.pop() as string).toLowerCase() : null;
};

export const getRachelFileExtensionLabel = (mediaType: string, filename?: string): string =>
  EXTENSION_LABEL_BY_MEDIA_TYPE[mediaType] ??
  (filename ? getExtensionFromFilename(filename) : null) ??
  '';

export const getRachelFileIcon = (mediaType: string): ElementType =>
  ICON_BY_MEDIA_TYPE[mediaType] ?? File;

export const isRachelImageMediaType = (mediaType: string): boolean =>
  mediaType.startsWith('image/');

export const formatRachelFileSize = (sizeInBytes: number): string => {
  if (sizeInBytes <= 0) {
    return `0 ${SIZE_UNITS[0]}`;
  }

  const unitIndex = Math.min(
    Math.floor(Math.log(sizeInBytes) / Math.log(BYTES_IN_KILOBYTE)),
    SIZE_UNITS.length - 1,
  );
  const value = sizeInBytes / BYTES_IN_KILOBYTE ** unitIndex;
  const roundedValue = unitIndex === 0 ? Math.round(value) : Number(value.toFixed(1));

  return `${roundedValue} ${SIZE_UNITS[unitIndex]}`;
};

export interface RachelFileNameParts {
  base: string;
  extension: string;
}

export const splitRachelFileName = (filename: string): RachelFileNameParts => {
  const lastDotIndex = filename.lastIndexOf('.');

  if (lastDotIndex <= 0) {
    return { base: filename, extension: '' };
  }

  return { base: filename.slice(0, lastDotIndex), extension: filename.slice(lastDotIndex) };
};
